import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { schemaGraph } from '../../utils/schema';
import { getStateBySlug } from '../../utils/states';

// /arizona/how-to-renew — the full step-by-step guide (Carson's "Guide Publishing Handoff",
// Week 1, Oct 2026). Targets the query "arizona real estate license renewal". The URL stays
// /arizona/how-to-renew because it already earns impressions; Kansas keeps the shorter
// state-driven page in StateHowToRenewPage. Facts verified against azre.gov on 2026-10-26.

const AZ = getStateBySlug('arizona');
const URL = canonicalUrl('/arizona/how-to-renew');
const PUBLISHED = '2026-10-28';
const H1 = 'How to Renew Your Arizona Real Estate License (Step by Step)';
const DESCRIPTION = 'Renew your Arizona real estate license in 5 steps: finish 24 CE hours, enter them in the ADRE Licensee Login, click Renew, pay $60. Late fees explained.';

// Rendered under "Frequently asked questions" AND emitted as FAQPage — keep identical.
const FAQ = [
  { q: 'How much does it cost to renew an Arizona real estate license?', a: `$60 for a salesperson or associate broker and $120 for a broker when renewed on time; $125 and $245 respectively if renewed late, within one year of expiration. CE courses are a separate cost — Steady Education's full 24-hour package is $${AZ.packagePrice}.` },
  { q: 'Can I renew my Arizona real estate license online?', a: 'Yes. The entire renewal — entering CE, answering the renewal questions and paying — is done in the ADRE Licensee Login at ptl.az.gov/dre. There\'s nothing to mail.' },
  { q: 'Does my CE provider report my hours to ADRE?', a: 'No. In Arizona the licensee enters each completed course in the Licensee Login using the course number and dates from the certificate. The renewal option only appears once every required category shows as met.' },
  { q: 'How many CE hours do I need to renew in Arizona?', a: '24 hours every two years for salespersons and associate brokers, in nine required categories plus a 3-hour elective block; 30 hours for designated, self-employed and delegated associate brokers.' },
  { q: 'What happens if I don\'t renew on time?', a: 'You can renew late for up to one year, paying the late fee ($125 salesperson / $245 broker), and you cannot practice until it\'s done. After one year the license expires permanently and you would need to re-qualify from the beginning.' },
];

const RELATED = [
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'The complete 24-hour breakdown by category.' },
  { url: '/how-to-submit-ce-to-adre', title: 'How to Submit CE to ADRE', desc: 'Step 2 in detail: what each Licensee Login field means.' },
  { url: '/arizona-license-expired-late-renewal', title: 'Your Arizona License Expired. Now What?', desc: 'The late fee, the one-year window, and the fastest fix.' },
  { url: '/when-does-my-arizona-license-expire', title: 'When Does My License Expire?', desc: 'How to find your exact renewal date.' },
];

const ADRE_LOGIN = 'https://ptl.az.gov/dre';
const Ext = ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;

export default function ArizonaHowToRenewGuide() {
  useSEO({
    title: 'Arizona Real Estate License Renewal: Step by Step | Steady',
    description: DESCRIPTION,
    url: URL,
    type: 'article',
    jsonLd: schemaGraph({
      mainSchemas: [
        {
          '@type': 'Article',
          headline: H1,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: { '@type': 'Organization', name: 'Steady Education' },
          publisher: { '@id': 'https://steadyeducation.com/#organization' },
          mainEntityOfPage: URL,
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQ.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ],
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: 'Arizona', url: 'https://steadyeducation.com/arizona' },
        { name: 'Resources', url: 'https://steadyeducation.com/arizona/resources' },
        { name: 'How to Renew', url: URL },
      ],
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="How to Renew"
      title={H1}
      subtitle="Renewing an Arizona real estate license comes down to three things: complete 24 hours of ADRE-approved continuing education, enter those hours yourself in the ADRE Licensee Login, and pay the renewal fee before your license expires."
      updatedLabel="Updated October 2026 · Verified against azre.gov"
      relatedGuides={RELATED}
    >
      <p>Nobody does the middle step for you — not your school, not your broker — and it's the step most people get wrong. This guide to Arizona real estate license renewal walks through all of it, in order, with what to click and what to have ready.</p>

      <h2>When does your Arizona real estate license need to be renewed?</h2>
      <p>Arizona licenses run on a two-year cycle and expire on the last day of your renewal month. Your expiration date is printed on your license and shown at the top of your record in the Licensee Login. Everything below has to be finished by that date.</p>
      <table>
        <thead><tr><th>What</th><th>When</th></tr></thead>
        <tbody>
          <tr><td>Renewal cycle</td><td>Every 2 years</td></tr>
          <tr><td>Expiration</td><td>Last day of your renewal month</td></tr>
          <tr><td>CE hours must be completed</td><td>Within the 24 months before you renew</td></tr>
          <tr><td>Late renewal allowed</td><td>Up to 1 year after expiration, with a late fee and no real estate activity in the meantime</td></tr>
          <tr><td>After 1 year</td><td>License cannot be renewed — you start over with pre-licensing, the exam and an original application</td></tr>
        </tbody>
      </table>
      <p>If you're not sure of your date, see <Link to="/when-does-my-arizona-license-expire">When does my Arizona license expire?</Link></p>

      <h2>What do you need before you start?</h2>
      <p>Have these ready and the whole thing takes about fifteen minutes:</p>
      <ol>
        <li><strong>24 hours of ADRE-approved CE</strong>, completed within the current two-year cycle, in the required categories: Agency Law (3), Commissioner's Standards (3), Contract Law (3), Disclosure (3), Fair Housing (3), Real Estate Legal Issues (3), Arizona Water (1), Firewise (1), Deed Fraud (1), and 3 hours of general/elective. Designated and self-employed brokers need 30 hours — the three Broker Management Clinics replace Commissioner's Standards. The full breakdown is in the <Link to="/arizona-real-estate-ce-requirements">Arizona CE requirements guide</Link>.</li>
        <li><strong>Your course completion certificates.</strong> You'll need three things from each one: the ADRE course number, the course start date and the course end date, exactly as printed.</li>
        <li><strong>Your Licensee Login credentials</strong> for the ADRE portal at <Ext href={ADRE_LOGIN}>ptl.az.gov/dre</Ext>. If you've never logged in, use "Forgot password" with the email ADRE has on file.</li>
        <li><strong>A card for the fee:</strong> $60 for a salesperson or associate broker renewed on time, $120 for a broker.</li>
      </ol>

      <h2>Step 1 — How do you finish your 24 hours of CE?</h2>
      <p>All 24 hours can be done online and self-paced; there is no classroom requirement. ADRE caps CE at 9 hours per day, so the fastest legitimate finish is three days. If your package doesn't explicitly include the three one-hour topics added in 2025 — Arizona Water, Firewise and Deed Fraud — it won't satisfy the current requirement, so check before you buy.</p>
      <p>Steady Education's complete package covers all 24 hours, including the 2025 topics, for ${AZ.packagePrice}, with certificates issued the moment you pass each course.</p>

      <h2>Step 2 — How do you enter each course in the ADRE Licensee Login?</h2>
      <p>This is the step people assume their school handles. It doesn't. Arizona schools don't report completions to ADRE; the licensee enters them.</p>
      <ol>
        <li>Sign in at <Ext href={ADRE_LOGIN}>ptl.az.gov/dre</Ext>.</li>
        <li>Open your license record and go to the continuing education section.</li>
        <li>For each course, enter the course number, start date and end date from the certificate, exactly as printed.</li>
        <li>Submit. The category progress on the same screen updates; every category needs to show as met before the renewal option appears.</li>
      </ol>
      <p>Enter courses as you finish them rather than all at once the week you expire — if a certificate has a typo or a course was approved under a different number, you want to find out with time to spare. Keep the certificate files; ADRE can audit, and you're responsible for proof. The detailed version of this step, with what each field means, is in <Link to="/how-to-submit-ce-to-adre">How to submit CE to ADRE</Link>.</p>

      <h2>Step 3 — Where is the "Renew My License" button?</h2>
      <p>Once ADRE's system shows your CE requirements as met, the Renew My License option appears in your Licensee Login. Select it and answer the renewal questions the portal asks (the same background and disclosure questions as your original application — answer them accurately; a "yes" doesn't block renewal, but a false "no" is a licensing violation).</p>

      <h2>Step 4 — How much is the Arizona renewal fee?</h2>
      <p>Pay by card in the portal. Per ADRE's current fee schedule:</p>
      <table>
        <thead><tr><th>License</th><th>On time</th><th>Late (within 1 year of expiration)</th></tr></thead>
        <tbody>
          <tr><td>Salesperson / associate broker</td><td>$60</td><td>$125</td></tr>
          <tr><td>Broker</td><td>$120</td><td>$245</td></tr>
        </tbody>
      </table>
      <p>ADRE's own wording: once payment is made, your license is renewed. There's no waiting period and nothing to mail.</p>

      <h2>Step 5 — How do you confirm your renewal went through?</h2>
      <p>Back on your license record, check that the expiration date has rolled forward two years. Save or print the confirmation and keep it with your certificates. If you hang your license with a broker, let the office know it's renewed — brokerages track this and some will pull you off the active roster at expiration.</p>

      <h2>What if you're already past your expiration date?</h2>
      <p>You can still renew for up to one year after expiration, but two things change: the fee goes up to the late amount above, and you cannot perform any real estate activity — showings, offers, negotiations, referral fees on new business — until the renewal is processed. Doing so anyway invites ADRE discipline on top of the fee. After one year the license is gone and you start over. The full picture is in <Link to="/arizona-license-expired-late-renewal">Arizona license expired: late renewal</Link>.</p>

      <h2>What are the common mistakes that delay renewals?</h2>
      <ul>
        <li><strong>Assuming the school reported your hours.</strong> It didn't. Check your Licensee Login a month before expiration and enter anything missing.</li>
        <li><strong>A package without Water, Firewise and Deed Fraud.</strong> Older packages still sell. Since January 1, 2025 all three are required.</li>
        <li><strong>Surplus hours in the wrong category.</strong> Extra Contract Law hours count toward your elective block but can't fill a missing Fair Housing hour; the portal will show which mandatory category is short.</li>
        <li><strong>Hours from the previous cycle.</strong> CE must be completed within the 24 months before you renew; nothing carries forward.</li>
        <li><strong>Waiting for a reminder.</strong> ADRE sends notices to the email on file, but the expiration date is yours to track. Put it in your calendar with a 60-day warning.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      {FAQ.map(f => (
        <div key={f.q} className="guide-faq-item">
          <h3>{f.q}</h3>
          <p>{f.a}</p>
        </div>
      ))}

      <div style={inlineCta}>
        <strong>Ready to renew?</strong> Complete Arizona CE Package — ${AZ.packagePrice} · all 24 hours · ADRE-approved, including Water, Firewise and Deed Fraud. Certificates issued the moment you pass.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>Get the package →</Link>
        </div>
      </div>

      <p className="guide-sources">
        Sources: <Ext href="https://azre.gov/all-adre-resources/fees">ADRE fee schedule</Ext>, <Ext href="https://azre.gov/divisions/licensing">ADRE Licensing division renewal instructions</Ext>, and the <Ext href={ADRE_LOGIN}>ADRE Licensee Login</Ext>. Verified October 2026.
      </p>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
